'use strict';

const Backbone = require('backbone');
const Marionette = require('backbone.marionette');
const _ = require('lodash');
const BlockLoading = require('../views/blockLoading.js');

module.exports = {
    isLoadingView: true,
    beforeConstruction: function () {
        const me = this;
        me._deferred = new Backbone.$.Deferred();

        if (!me.loadData) {
            me._deferred.resolve();
        }

        _.defaults(me, {
            LoadingView: BlockLoading,
            loadingViewTemplate: '#shellPanelMinimalLoadingTemplate'
        });

        me.loadingChildViews = [];
        me.pendingLoadingChildViews = [];
        me.pendingForcedLoadingChildViews = [];
        me.forcedLoadingChildViews = [];

        me._parentLoadingView = me;

        me.listenTo(me, 'destroy', _.bind(me._onDestroy, me));
    },

    finishLoading: function () {
        const me = this;

        me.removeLoadingView()
            .done(function () {
                me._onLoadingFinished();
            });
    },

    loadedRender: function () {
        const me = this;

        me.waitForAsyncRender(function () {
            // View was destroyed before content loaded. Do not render
            if (me.isDestroyed) {
                return;
            }
            if (me.isReloading && me.isRendered && me.refresh) {
                me._refreshView();
            } else {
                me._renderRealView();
            }
        });
    },

    onLoadDataError: _.noop,

    render: function () {
        const me = this;

        me.isRendering = true;

        me.getLoadingView().pushChildView(me);

        if (!me.isDestroyed && (!me.isReloading || !me.refresh)) {
            _.result(me, 'onBeforeRenderLoaded');
        }

        me._deferred
            .done(_.bind(me.loadedRender, me))
            .fail(_.bind(me.onLoadDataError, me));

        if (!me.isDataLoaded()) {
            me._load();
        }

        return me;
    },

    isLoadingComplete: function () {
        const me = this;
        return me.isDataLoaded() && !me.pendingLoadingChildViews.length;
    },

    /*
     * Returns true if this view is the visual loading view for the passed
     * in view
     */
    isVisualLoadingParentOfView: function (view) {
        const me = this;
        return view._parentLoadingView === me;
    },

    hasForcedLoadingView: function () {
        const me = this;
        return Boolean(me._trueParentLoadingView);
    },

    /*
     * When a view starts loading it pushes itself to its parent view,
     * which is responsible for displaying the loading view itself
     */
    pushChildView: function (view) {
        const me = this;

        if (view.hasForcedLoadingView()) {
            view.getParentLoadingView().pushForcedChildView(view);
        }

        me.pendingLoadingChildViews = _.union(me.pendingLoadingChildViews, [view]);
        me.loadingChildViews = _.union(me.loadingChildViews, [view]);
        me._onPendingLoadingChildViewsChange();
    },

    pushForcedChildView: function (view) {
        const me = this;
        me.pendingForcedLoadingChildViews = _.union(me.pendingForcedLoadingChildViews, [view]);
        me.forcedLoadingChildViews = _.union(me.forcedLoadingChildViews, [view]);
        me._onForcedLoadingChildViewsChange();
    },

    _onForcedLoadingChildViewsChange: function () {
        const me = this;
        // If all child views have finished loading, trigger finish:loading
        if (!me.pendingLoadingChildViews.length && !me.pendingForcedLoadingChildViews.length) {
            me.finishLoading();
        }
    },

    _onPendingLoadingChildViewsChange: function () {
        const me = this;

        // If all child views have finished loading, trigger finish:loading
        if (!me.pendingLoadingChildViews.length && !me.pendingForcedLoadingChildViews.length) {
            return me.finishLoading();
        }

        if (me.pendingLoadingChildViews.length) {
            me.showLoadingView();
        }

        if (!me.pendingLoadingChildViews.length) {
            return me.removeLoadingView();
        }
    },

    _onDestroy: function () {
        const me = this;
        me.getLoadingView()._onChildDestroy(me);

        if (me.hasForcedLoadingView()) {
            me.getParentLoadingView()._onForcedLoadingChildViewsChange(me);
        }
    },

    _onRenderFinished: function () {
        const me = this;
        me.trigger('render:finished');
        me.getLoadingView()._onChildRenderFinish(me);
    },

    _onChildDestroy: function (child) {
        const me = this;
        me.pendingLoadingChildViews = _.without(me.pendingLoadingChildViews, child);
        me.loadingChildViews = _.without(me.loadingChildViews, child);
        me._onPendingLoadingChildViewsChange();
    },

    _onChildRenderFinish: function (child) {
        const me = this;
        me.pendingLoadingChildViews = _.without(me.pendingLoadingChildViews, child);
        me._onPendingLoadingChildViewsChange();
    },

    _onForcedLoadingChildLoadFinish: function (child) {
        const me = this;

        me.pendingForcedLoadingChildViews = _.without(me.pendingForcedLoadingChildViews, child);
        me._onForcedLoadingChildViewsChange();
    },

    _onForcedLoadingChildDestroy: function (child) {
        const me = this;
        me.pendingForcedLoadingChildViews = _.without(me.pendingForcedLoadingChildViews, child);
        me.forcedLoadingChildViews = _.without(me.forcedLoadingChildViews, child);
        me._onForcedLoadingChildViewsChange();
    },

    _onLoadingFinished: function () {
        const me = this;

        me.trigger('loading:finished');

        if (me.hasForcedLoadingView()) {
            me.getParentLoadingView()._onForcedLoadingChildLoadFinish(me);
        }
    },

    // If the view is rendering asynchronously wait for it to complete
    // rendering before signaling completion
    waitForAsyncRender: function (done) {
        const me = this;
        if (me.isPendingAsyncRender()) {
            me.listenToOnce(me, 'render:async', done);
        } else {
            done();
        }
    },

    _refreshView: function () {
        const me = this;
        me.refresh();

        me.waitForAsyncRender(function () {
            me.isRendering = false;
            me.isReloading = false;

            me.requestFrame(function () {
                me._onRenderFinished();
            });
        });
    },

    _renderRealView: function () {
        const me = this;
        me.baseRender.apply(me);

        me.waitForAsyncRender(function () {
            me.isRendering = false;
            me.isReloading = false;

            me.requestFrame(function () {
                me.triggerMethod('render:loaded');

                me.requestFrame(function () {
                    me._onRenderFinished();
                });
            });

        });
    },

    requestFrame: function (fn) {
        const me = this;

        window.requestAnimationFrame(function () {
            if (!me.isDestroyed) {
                fn.call(me);
            }
        });
    },

    /**
     * This method will call `fn` if and only if the view is not destroyed, and
     * it has been rendered. This is to alleviate the possibility of a frame
     * firing WHILE the view is rendering, which will often yield
     * Null Reference Exceptions with `ui` elements.
     * @param {Function} fn
     */
    requestRenderedFrame: function (fn) {
        const me = this;

        me.requestFrame(function () {
            if (me.isRendered) {
                fn.call(me);
            }
        });
    },

    hasLoadingView: function () {
        const me = this;
        return me === me.getLoadingView() && _.get(me, '_parent.isLoadingRegion', false);
    },

    showLoadingView: function (options) {
        const me = this;

        if (me.getOption('disableLoadingView') || _.get(options, 'disableLoadingView')) {
            return;
        }

        if (me.loadingView && !me.loadingView.isVisible()) {
            me.loadingView.showForView(me);
        }
    },

    removeLoadingView: function () {
        const me = this;

        const deferred = new Backbone.$.Deferred();
        if (Marionette.isNodeAttached(me.el)) {
            removeLoadingViewAndNotifyChildren();
        } else {
            me.listenToOnce(me, 'attach', removeLoadingViewAndNotifyChildren);
        }

        return deferred;

        // Try to avoid triggerMethod b/c it is slow
        function triggerAttachLoaded(childView) {
            if (childView.onAttachLoaded) {
                return childView.onAttachLoaded();
            }

            childView.trigger('attach:loaded');
        }

        function removeLoadingViewAndNotifyChildren() {
            if (me.loadingView && !me.loadingView.isVisible()) {
                return deferred.resolve();
            } else if (me.loadingView) {
                me.loadingView.hideForView(me);
            }

            // Do not fire attach:loaded if a parent loading view is still hidden
            if (me.hasForcedLoadingView() &&
                !me.getParentLoadingView().isLoadingComplete() &&
                me.getParentLoadingView().hasLoadingView()) {

                return deferred.resolve();
            }

            me.requestFrame(function () {

                /*
                 * Notify child views that they are now shown
                 * This is important for views that need to calculate their width
                 * (i.e. d3 views)
                 */
                let deferreds = _.map(me.loadingChildViews, triggerAttachLoaded);

                deferreds = deferreds.concat(_.map(me.forcedLoadingChildViews, function (childView) {

                    /*
                     * Forced loading views might not be finished loading yet, so
                     * check before triggering attach:loaded on them
                     */
                    if (childView.isLoadingComplete()) {

                        /*
                         * Trigger attach:loaded on all forceLoaded child views
                         * b/c they have finally been un-hidden
                         */
                        return _.map(childView.loadingChildViews, triggerAttachLoaded);
                    }
                }));

                Backbone.$.when.apply(null, _.compact(_.flattenDeep(deferreds))).done(deferred.resolve);
            });
        }
    },

    getLoadingView: function () {
        const me = this;
        return me._parentLoadingView;
    },

    getParentLoadingView: function () {
        const me = this;
        return me._trueParentLoadingView;
    },

    onLoading: function () {
        const me = this;
        me._load();
    },

    reload: function (options) {
        const me = this;

        if (me.loadData) {
            me._deferred.reject();
            me._deferred = new Backbone.$.Deferred();
        }

        me.isReloading = true;

        me.requestFrame(function () {
            me.render(options);
        });
    },

    isDataLoaded: function () {

        const me = this;
        return (me._deferred.state() === 'resolved');
    },

    _load: function () {
        const me = this;
        if (me.loadData && me.loadData.length > 0) {
            return me.loadData(me._deferred.resolve);
        }

        // When we call _load multiple times, we only want to take action on the last promise resolution.
        const loadId = me._currentLoadId = _.uniqueId();

        me.loadData()
            .done(function () {
                if (loadId === me._currentLoadId) {
                    return me._deferred.resolve();
                }
            })
            .fail(function (error) {
                if (loadId === me._currentLoadId) {
                    return me._deferred.reject(error);
                }
            });
    }
};
